import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  PriceBoxBottomLabel,
  PriceBoxTopLabel,
  PriceValue,
  PromoBadges
} from "@silversea-ssc/ui-components-react";
import { CruiseCardModel } from "components/CruiseCard/types";
import { NewEcommerceButton } from "components/ECommerceButtons";
import React, { MouseEventHandler, useEffect } from "react";
import * as styles from "./CruiseDrawerPriceAndOffers.module.scss";
import { arrayIsNotEmpty } from "src/utils/checks";
import clsx from "clsx";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { perGuestLabel, transformPriceFrom } from "components/Prices/utils";
import RefundableLabel from "components/CruiseDrawer/components/RefundableLabel/RefundableLabel";
import { useStaticFareLabels } from "features/fare/use-static-fare-labels";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import { useExperimentTag } from "src/ab-test-experiments/client/use-experiment-tag";
import { CruiseDrawerSaveThisQuoteDisclaimer } from "components/CruiseDrawer/components/CruiseDrawerSaveThisQuoteDisclaimer/CruiseDrawerSaveThisQuoteDisclaimer";

type Props = {
  cruiseCard: CruiseCardModel;
  raqPagePath: string | undefined;
  onViewItineraryClick: MouseEventHandler<HTMLAnchorElement> | undefined;
  ecommerceStatus: ECommerceStatus;
  openWebQuoteModal?: () => void;
};
const CruiseDrawerPriceAndOffers: React.FC<Props> = ({
  cruiseCard,
  raqPagePath,
  onViewItineraryClick,
  ecommerceStatus,
  openWebQuoteModal
}) => {
  const { isSticky, ref } = useInnerBarIsSticky();
  const { fareCodeLabel } = useStaticFareLabels();
  const promoBadges = cruiseCard.promosBadge;
  const geolocation = useGeolocation();
  const { t } = useLingui();
  const isWaitlist = cruiseCard.leadPriceFrom?.type == "waitlist";

  const transformedPrice = transformPriceFrom(
    t,
    cruiseCard.leadPriceFrom,
    geolocation.defaultCurrency,
    true,
    "without-from",
    fareCodeLabel
  );

  const hasPromoBadges = arrayIsNotEmpty(promoBadges);

  const webQuoteExperimentActive = useExperimentTag("web-quote-modal");
  const isEcommerceActive =
    ecommerceStatus.type === "ecommerce" ||
    ecommerceStatus.type === "priceconfig";

  return (
    <>
      {hasPromoBadges && (
        <div className={styles.promoBadges}>
          <PromoBadges promos={promoBadges} variant="normal" />
        </div>
      )}
      <div ref={ref}></div>
      <section
        className={clsx(styles.priceAndOffers, {
          [styles.isSticky]: isSticky,
          [styles.noPromos]: !hasPromoBadges
        })}
      >
        <div>
          <div className={styles.priceRow}>
            <PriceBoxTopLabel uppercase>
              {t({ id: "from-price" })}
            </PriceBoxTopLabel>
            {transformedPrice && (
              <PriceValue
                price={transformedPrice}
                chipBackgroundColor="neutral05"
                priceType="primary"
              />
            )}
            {isWaitlist ? (
              <PriceBoxBottomLabel uppercase>
                {t(perGuestLabel())}
              </PriceBoxBottomLabel>
            ) : undefined}
          </div>
          {isWaitlist ? undefined : (
            <PriceBoxBottomLabel uppercase>
              <div>{transformedPrice?.fareLabel}</div>
            </PriceBoxBottomLabel>
          )}
        </div>

        <div className={styles.ctas}>
          <NewEcommerceButton
            raqUrl={raqPagePath}
            ecommerceStatus={ecommerceStatus}
            variant="accent"
            size="small"
            full
            overrideEcommerceLabel={
              webQuoteExperimentActive?.segmentId === "b" ? (
                <Trans>Hold or Book your Cruise</Trans>
              ) : undefined
            }
          />

          <Button
            renderAs="a"
            onClick={onViewItineraryClick}
            href={cruiseCard.detailUrl}
            variant={
              webQuoteExperimentActive?.segmentId === "b" ? "text" : "secondary"
            }
            target="_blank"
            dataContentPiece="cruise-drawer-view-itinerary"
            size="small"
          >
            <Trans>View Itinerary</Trans>
          </Button>
        </div>

        <RefundableLabel
          cruiseCode={cruiseCard.voyageCode}
          priceFrom={cruiseCard.leadPriceFrom}
        />
      </section>
      {webQuoteExperimentActive?.segmentId == "b" && isEcommerceActive && (
        <CruiseDrawerSaveThisQuoteDisclaimer
          openWebQuoteModal={openWebQuoteModal}
        />
      )}
    </>
  );
};
export default CruiseDrawerPriceAndOffers;

const useInnerBarIsSticky = () => {
  const [isSticky, setIsSticky] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    const element = ref.current;
    try {
      // eslint-disable-next-line compat/compat
      observer = new IntersectionObserver(
        ([e]) => {
          setIsSticky(!e.isIntersecting);
        },
        { threshold: [1] }
      );

      if (element) {
        observer.observe(element);
      }
    } catch (e) {
      console.error("IntersectionObserver not supported", e);
    }
    return () => {
      if (element) observer?.unobserve(element);
    };
  }, []);

  return { isSticky, ref };
};
